/**
 * Normalizers for the BE-332 `DecisionSupportQuery.visitReassignment` response.
 *
 * Produces the shared `NormalizedSlotResult` shape so the new V3 flow can feed
 * the existing data-agnostic `DecisionSupportRecommendations` table.
 *
 * Different from `clinicianRecommendations.utils.tsx` and `dateRecommendations.utils.tsx`
 * (V2-era normalizers) in two ways:
 *  1. Columns come from server-provided `RecommendationDefinitions` (no hardcoded factor codes).
 *  2. All scoring factors use uniform 0-100 `sortValue` — server-side inversion handles
 *     "lower is better" factors, so cells render with a single rule (no `isInverse`/`range`).
 */
import {
    IncompatibleClinicianResultFragment,
    IncompatibleDateResultFragment,
    RecommendationDefinitionsFragment,
} from '@/components/decisionSupport/queries/visitReassignment.gql.types';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';

import { ConstraintCell } from '../tables/ConstraintCell';
import { NotAvailableCell } from '../tables/NotAvailableCell';
import { RecommendationCell } from '../tables/RecommendationCell';
import { EMPTY_RESULT, NormalizedSlotResult, RecommendationRow } from './decisionSupport.utils';

// Structural minimums for the recommendation items these normalizers read. They
// intentionally omit `earnedBadges` (never read here) so the badge-free
// `visitAssignment` fragments satisfy the same normalizers as the reassignment
// ones — a single normalizer serves both flows. `isApplicable` is optional so
// the reassignment fragments (which don't select it) still satisfy this shape;
// absent ⇒ treated as applicable.
type ScoredItemLike = {
    rank: number;
    scoringFactors: readonly {
        code: string;
        sortValue: number;
        displayValue: string;
        isApplicable?: boolean;
    }[];
};
type ClinicianRecItemLike = ScoredItemLike & {
    clinician: { id: string; fullName: string; externalIds: readonly { value: string }[] };
};
type DateRecItemLike = ScoredItemLike & { date: Date };

type ScoringFactors = RecommendationDefinitionsFragment['scoringFactors'];

const sortScoringFactors = (defs: ScoringFactors): ScoringFactors =>
    defs.slice().sort((a, b) => a.sortIndex - b.sortIndex);

const factorsFromScoringResults = (
    scoringFactors: readonly {
        code: string;
        sortValue: number;
        displayValue: string;
        isApplicable?: boolean;
    }[],
): RecommendationRow['factors'] => {
    const factors: RecommendationRow['factors'] = {};
    scoringFactors.forEach((sf) => {
        factors[sf.code] = {
            sortValue: sf.sortValue,
            displayValue: sf.displayValue,
            isSatisfied: null,
            isApplicable: sf.isApplicable,
        };
    });
    return factors;
};

const factorsFromHardConstraintResults = (
    constraintResults: readonly { code: string; isSatisfied: boolean }[],
): RecommendationRow['factors'] => {
    const factors: RecommendationRow['factors'] = {};
    constraintResults.forEach((cr) => {
        factors[cr.code] = {
            sortValue: null,
            displayValue: '',
            isSatisfied: cr.isSatisfied,
        };
    });
    return factors;
};

const buildRecommendedColumns = (scoringFactors: ScoringFactors): ColumnDef<RecommendationRow>[] =>
    sortScoringFactors(scoringFactors).map((def) => ({
        id: def.code,
        header: def.displayName,
        cell: ({ row }) => {
            const factor = row.original.factors[def.code];
            if (!factor || factor.sortValue === null) {
                return null;
            }
            // Not-applicable factors render a neutral gray "N/A" instead of the
            // green→red score, so a non-applicable factor isn't read as "bad".
            if (factor.isApplicable === false) {
                return <NotAvailableCell label={factor.displayValue || undefined} />;
            }
            return <RecommendationCell value={factor.sortValue} />;
        },
    }));

const buildNotRecommendedColumns = (
    hardConstraints: readonly { code: string; displayName: string }[],
): ColumnDef<RecommendationRow>[] =>
    hardConstraints.map((def) => ({
        id: def.code,
        header: def.displayName,
        cell: ({ row }) => {
            const factor = row.original.factors[def.code];
            if (!factor || factor.isSatisfied === null) {
                return null;
            }
            return <ConstraintCell isSatisfied={factor.isSatisfied} />;
        },
    }));

// ────────────────────────────────────────────────────────────────────────────
// Clinician
// ────────────────────────────────────────────────────────────────────────────

const normalizeClinicianRecommendationItem = (item: ClinicianRecItemLike): RecommendationRow => ({
    id: item.clinician.id,
    label: item.clinician.fullName,
    externalIds: item.clinician.externalIds.map((e) => e.value),
    recommendationRank: item.rank,
    factors: factorsFromScoringResults(item.scoringFactors),
});

const normalizeIncompatibleClinician = (
    ic: IncompatibleClinicianResultFragment,
): RecommendationRow => ({
    id: ic.clinician.id,
    label: ic.clinician.fullName,
    externalIds: ic.clinician.externalIds.map((e) => e.value),
    recommendationRank: Infinity,
    factors: factorsFromHardConstraintResults(ic.hardConstraintResults),
});

type ClinicianRecInput = {
    recommendations: ClinicianRecItemLike[];
    incompatibleClinicians: IncompatibleClinicianResultFragment[];
};

export const normalizeVisitReassignmentClinician = (
    clinicianRecommendation: ClinicianRecInput | null,
    definitions: RecommendationDefinitionsFragment | null,
): NormalizedSlotResult => {
    if (!clinicianRecommendation || !definitions) {
        return EMPTY_RESULT;
    }
    return {
        recommended: clinicianRecommendation.recommendations.map(
            normalizeClinicianRecommendationItem,
        ),
        notRecommended: clinicianRecommendation.incompatibleClinicians.map(
            normalizeIncompatibleClinician,
        ),
        recommendedColumns: buildRecommendedColumns(definitions.scoringFactors),
        notRecommendedColumns: buildNotRecommendedColumns(definitions.hardConstraints),
    };
};

// ────────────────────────────────────────────────────────────────────────────
// Date
// ────────────────────────────────────────────────────────────────────────────

const formatDateLabel = (date: Date): string => moment(date).format('ddd MMM D');

const normalizeDateRecommendationItem = (item: DateRecItemLike): RecommendationRow => {
    const iso = moment(item.date).format('YYYY-MM-DD');
    return {
        id: iso,
        label: formatDateLabel(item.date),
        recommendationRank: item.rank,
        factors: factorsFromScoringResults(item.scoringFactors),
    };
};

const normalizeIncompatibleDate = (id: IncompatibleDateResultFragment): RecommendationRow => {
    const iso = moment(id.date).format('YYYY-MM-DD');
    return {
        id: iso,
        label: formatDateLabel(id.date),
        recommendationRank: Infinity,
        factors: factorsFromHardConstraintResults(id.hardConstraintResults),
    };
};

type DateRecInput = {
    recommendations: DateRecItemLike[];
    incompatibleDates: IncompatibleDateResultFragment[];
};

export const normalizeVisitReassignmentDate = (
    dateRecommendation: DateRecInput | null,
    definitions: RecommendationDefinitionsFragment | null,
): NormalizedSlotResult => {
    if (!dateRecommendation || !definitions) {
        return EMPTY_RESULT;
    }
    return {
        recommended: dateRecommendation.recommendations.map(normalizeDateRecommendationItem),
        notRecommended: dateRecommendation.incompatibleDates.map(normalizeIncompatibleDate),
        recommendedColumns: buildRecommendedColumns(definitions.scoringFactors),
        notRecommendedColumns: buildNotRecommendedColumns(definitions.hardConstraints),
    };
};
